import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Sparkles, Calendar, MessageCircle, Users, ShieldCheck, TrendingUp, Workflow, BarChart3, Mail, Search, Megaphone, Image, Send, Target, RefreshCcw, Lock, Linkedin} from "lucide-react";
import { SectionLabel, HighlightText, ToolPill, SectionHeading } from "@/components/Bits";
import FAQAccordion from "@/components/FAQAccordion";
import FinalCTA from "@/components/FinalCTA";
import ContactForm from "@/components/ContactForm";
import AnimatedStat from "@/components/AnimatedStat";
import { COMMON_FAQ, HOME_TOOLS } from "@/lib/content";
import { CALENDLY_URL } from "@/lib/api";

const SPECIALIZATIONS = [ 
  { title: "EA/VA's", desc: "Calendar, inbox, travel, CRM, EA/CXO support.", icon: Users, link: "/virtual-assistant-services" },
  { title: "Bookkeeping Professionals", desc: "AP/AR, reconciliations, invoicing, QuickBooks, Xero.", icon: BarChart3, link: "/bookkeeping-services" },
  { title: "Marketing Professionals", desc: "Social media, email campaigns, SEO, Klaviyo, paid ads.", icon: Megaphone, link: "/marketing-assistance" },
  { title: "Customer Support Pros", desc: "Live chat, email, support, Zendesk, Gorgias, Freshdesk.", icon: MessageCircle, link: "/customer-support" },
];

const PARTNERS = [
  { name: "Sushant Gupta", role: "Co-Founder & CEO", initials: "SG", linkedin:"linkedin.com/in/theofficialsushantgupta/", image: "https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/xj0phsvh_Sushant_Gupta.png" },
  { name: "Ankita Dewani", role: "Co-Founder & CBO", initials: "AD", linkedin:"linkedin.com/in/ankitadewani/", image: "https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/0oih82ar_Ankita_diwani.png" },
];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>#1 Virtual Assistant Services | Hire Virtual Assistant | Aidify</title>
        <meta name="description" content="Aidify is a trusted Virtual Assistant Company offering expert Virtual Assistant Services to businesses worldwide. Hire Virtual Assistant today and save up to 50% on operational costs. Get Started Now!" />
      </Helmet>
      {/* HERO */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <div className="mb-6">
          <SectionLabel testid="home-hero-label">Trusted by 500+ modern businesses</SectionLabel>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[88px] leading-[0.95]">
              Global Talent,
              <br />
              <span className="font-serif-italic font-normal">Local </span>
              <HighlightText><span className="font-serif-italic font-normal">Accountability.</span></HighlightText>
            </h1>
            <p className="mt-7 max-w-xl text-forest-400 text-base md:text-[17px] leading-relaxed">
              Get the reliability of a local partner with the scale of a global workforce. We provide vetted, full-time talent for Marketing, Bookkeeping, Customer Support and Administrative VA roles. All backed by US oversight.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#target-section" className="btn-primary" data-testid="hero-cta-primary">
                Get a Virtual Assistant Now <ArrowRight size={16} />
              </a>
              <Link to="/how-it-works" className="btn-ghost" data-testid="hero-cta-secondary">
                See how it works <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            {/* Image collage like Figma */}
            <div className="relative h-[360px] md:h-[420px]" data-reveal>
              <img
                src="/teamImages/Homepage-imageone.jpeg"
                alt="Professional virtual assistant at work"
                className="absolute top-0 right-0 w-[78%] h-[60%] object-cover rounded-2xl shadow-lg"
              />
              <img
                src="/teamImages/homepage-imagetwo.png"
                alt="Team collaboration"
                className="absolute bottom-0 left-0 w-[68%] h-[55%] object-cover rounded-2xl shadow-lg border-4 border-cream-100"
              />
              {/* Floating stats card */}
              <div className="absolute -top-2 left-2 aidify-card px-4 py-2 flex items-center gap-2 shadow-md">
                <span className="w-2 h-2 rounded-full bg-sage-500 animate-pulse" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-forest-500 font-semibold">Live · 24/7 US Oversight</span>
              </div>
              <div className="absolute bottom-3 right-2 aidify-card-dark px-4 py-3 shadow-md">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-extrabold text-3xl text-cream-100">2h</span>
                  <span className="text-[10px] uppercase tracking-wider text-cream-100/70">to match</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tools strip */}
        <div className="mt-14 border-y border-forest-500/10 py-5 overflow-hidden">
          <div className="flex items-center gap-8">
            <span className="text-[11px] uppercase tracking-[0.2em] text-forest-400/70 shrink-0 whitespace-nowrap">Tools we integrate with :</span>
            <div className="flex-1 overflow-hidden">
              <div className="flex gap-10 marquee-track whitespace-nowrap">
                {[...HOME_TOOLS, ...HOME_TOOLS].map((t, i) => (
                  <span key={i} className="text-forest-500 font-medium text-sm">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THREE WAYS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="three-ways">
        <SectionHeading
          label="Why Aidify"
          title="Three ways we help you"
          italic="move faster."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {[
            {
              num: "01 . Freedom",
              title: "Focus on Growth.",
              italic: "We'll handle the rest.",
              desc: "Delegate your daily operations to dedicated offshore specialists. We provide the talent and infrastructure you need to reclaim your schedule and focus on business innovation.",
              bullets: ["Dedicated specialists", "Infrastructure included", "Reclaim your schedule"],
            },
            {
              num: "02 . Standard",
              title: "Global Efficiency",
              italic: "US standards.",
              desc: "Scale your team with vetted offshore talent in Engineers, Marketing, Bookkeeping and Customer Success. Every role is overseen by our veteran US management team, ensuring domestic-level accountability and precision.",
              bullets: ["Vetted offshore talent", "US partner accountability", "Domestic accountability"],
              dark: true,
            },
            {
              num: "03 . Speed",
              title: "Elite Talent",
              italic: "Immediate effect",
              desc: "Skip the hiring cycle. We use 25 years of recruiting expertise to provide the top 1% of global offshore professionals. Get a dedicated partner ready to integrate into your workflow on day one.",
              bullets: ["Top 1% talent", "25 years of expertise", "Integrated from day one"],
            },
          ].map((c, i) => (
            <div
              key={i}
              className={`p-8 flex flex-col ${c.dark ? "aidify-card-dark md:-my-4 md:py-12 shadow-2xl" : "aidify-card"}`}
              data-reveal
            >
              <div className="flex items-center justify-between mb-12">
                <span className={`text-sm font-medium ${c.dark ? "text-cream-100/80" : "text-forest-400/80"}`}>{c.num}</span>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center ${c.dark ? "bg-forest-500/40" : "bg-sage-100"}`}>
                  <Sparkles size={14} className={c.dark ? "text-sage-200" : "text-sage-500"} />
                </span>
              </div>
              <h3 className={`font-display font-extrabold text-3xl md:text-4xl leading-tight ${c.dark ? "text-cream-100" : "text-forest-600"}`}>{c.title}</h3>
              <p className={`font-serif-italic text-2xl mt-2 ${c.dark ? "text-cream-100/95" : "text-forest-500"}`}>{c.italic}</p>
              <div className={`my-7 border-t ${c.dark ? "border-cream-100/15" : "border-forest-500/15"}`} />
              <p className={`text-[15px] leading-relaxed ${c.dark ? "text-cream-100/80" : "text-forest-400"}`}>{c.desc}</p>
              <ul className={`mt-7 space-y-3 text-[15px] flex-1 ${c.dark ? "text-cream-100/90" : "text-forest-500"}`}>
                {c.bullets.map((b, j) => (
                  <li key={j} className="flex items-center gap-3">
                    <CheckCircle2 size={16} className={c.dark ? "text-sage-200" : "text-sage-500"} />
                    {b}
                  </li>
                ))}
              </ul>
              <Link to="/how-it-works" className={`mt-8 inline-flex items-center gap-1 text-sm font-medium ${c.dark ? "text-cream-100" : "text-forest-600"}`}>
                Learn more <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* RIGHT PEOPLE — animated redesign */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24 relative" data-testid="right-people">
        {/* Decorative floating shapes */}
        <div className="pointer-events-none absolute -top-12 right-10 w-32 h-32 sage-blob opacity-50 float-y" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-6 left-1/3 w-20 h-20 sage-blob opacity-30 float-y-slow" aria-hidden="true" />

        <SectionHeading
          label="US-managed virtual assistants"
          title="Right People."
          italic="Ready Now."
          description="Stop searching. Our professionals across Bookkeeping, Marketing, Executive Support and Customer Success are pre-vetted, US-standard trained and ready to plug into your business within 2 hours. Every professional is backed by a US business partner who takes full local accountability."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative">
          {/* Animated stats column */}
          <div className="lg:col-span-5 space-y-4">
            {[
              { value: 2, suffix: " hrs", label: "Ready to start", note: "From conversation to working professional", sage: true },
              { value: 1, prefix: "Top ", suffix: "%", label: "Global talent", note: "Vetted across 20+ countries", dark: true },
              { value: 24, suffix: "/7", label: "US oversight", note: "A named partner. A real phone line.", sage: false },
            ].map((s, i) => (
              <div
                key={i}
                data-reveal
                data-i={i + 1}
                className={`count-pop is-visible p-6 flex items-start gap-5 ${
                  s.dark ? "aidify-card-dark" : s.sage ? "aidify-card-sage" : "aidify-card"
                } glow-on-hover relative overflow-visible`}
              >
                <div className="flex flex-col flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className={`font-display font-extrabold text-5xl md:text-6xl count-pop-target leading-none ${s.dark ? "text-cream-100" : "text-forest-600"}`}>
                      <AnimatedStat value={s.value} prefix={s.prefix} suffix={s.suffix} />
                    </span>
                  </div>
                  <div className={`mt-4 text-[11px] uppercase tracking-[0.22em] font-medium ${s.dark ? "text-cream-100/60" : "text-forest-400/70"}`}>{s.label}</div>
                  <div className={`mt-1 text-sm ${s.dark ? "text-cream-100/75" : "text-forest-500"}`}>{s.note}</div>
                </div>
                {/* Animated ring on the right */}
                <div className="relative w-10 h-10 shrink-0">
                  <span className={`absolute inset-0 rounded-full border-2 ${s.dark ? "border-cream-100/30" : "border-forest-500/20"} spin-slow`} style={{ borderTopColor: s.dark ? "#CFDCAA" : "#849756" }} />
                  <span className={`absolute inset-2 rounded-full ${s.dark ? "bg-sage-200" : "bg-forest-600"}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Specializations column */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-forest-400/70">Specializations</div>
              <div className="flex items-center gap-2 text-[11px] text-forest-500">
                <span className="pulse-dot" /> <span>Bench available now</span>
              </div>
            </div>
            <div className="space-y-3">
              {SPECIALIZATIONS.map((s, i) => (
                <Link
                  key={i}
                  to={s.link}
                  data-testid={`spec-${i}`}
                  data-reveal
                  data-i={i + 1}
                  className="aidify-card p-5 md:p-6 flex items-center justify-between gap-4 group glow-on-hover relative"
                >
                  <div className="flex items-center gap-5 min-w-0">
                    <div className="relative w-14 h-14 shrink-0">
                      <div className="absolute inset-0 rounded-full bg-sage-200 group-hover:bg-sage-300 transition-colors duration-300" />
                      <div className="absolute inset-0 rounded-full border border-sage-300 group-hover:scale-125 transition-transform duration-500 opacity-0 group-hover:opacity-100" />
                      <div className="relative w-full h-full flex items-center justify-center">
                        <s.icon size={20} className="text-forest-600 icon-magnetic" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-display font-bold text-forest-600 text-lg md:text-xl group-hover:translate-x-0.5 transition-transform duration-300">
                        {s.title}
                      </h4>
                      <p className="text-sm text-forest-400 mt-0.5 truncate">{s.desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="hidden md:inline text-[11px] uppercase tracking-[0.18em] text-forest-400/60 group-hover:text-forest-600 transition-colors">
                      Explore
                    </span>
                    <span className="w-9 h-9 rounded-full border border-forest-500/15 flex items-center justify-center group-hover:bg-forest-600 group-hover:border-forest-600 transition-all">
                      <ArrowRight size={16} className="text-forest-500 group-hover:text-cream-100 group-hover:translate-x-0.5 transition-all" />
                    </span>
                  </div>
                </Link>
              ))}

              {/* Mini stat strip */}
              <div className="aidify-card-dark p-5 flex items-center justify-between mt-2" data-reveal data-i="5">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-sage-200 pulse-dot" />
                  <span className="text-cream-100/85 text-sm">Don't see your function?</span>
                </div>
                <Link to="/contact" className="link-underline text-sage-200 text-sm font-medium">
                  Tell us what you need <ArrowRight size={14} className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COFFEE CTA */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="coffee-cta">
        <div className="aidify-card-dark p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-reveal>
          <div className="lg:col-span-8">
            <span className="section-pill !bg-forest-500 !text-cream-100/80 !border-forest-400">— Take the conversation further</span>
            <h3 className="mt-5 font-display font-extrabold text-3xl md:text-5xl text-cream-100 leading-tight">
              Let's connect over <span className="font-serif-italic font-normal text-sage-200">a virtual coffee.</span>
            </h3>
            <p className="mt-4 text-cream-100/70 max-w-xl text-sm leading-relaxed">
              Book a casual 15-minute chat with Sweety, our Business Head. Discuss your growth goals and how our vetted VAs can fit seamlessly into your operations and all this over your favorite brew. No pressure, just insight.
            </p>
          </div>
          <div className="lg:col-span-4 flex justify-end">
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-on-dark" data-testid="coffee-cta-btn">
              <Calendar size={16} /> Book a meet with Sweety now
            </a>
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="how-we-work">
        <SectionHeading
          label="How we work"
          title="Simple to start."
          italic="Faster than you think."
          description="From your first conversation to having a professional in your workflow, every step is built around your time."
        />
        <div className="aidify-card overflow-hidden">
          {[
            { n: "01", t: "Share Your Needs", tag: "Discovery", d: "A quick conversation. No long forms or job descriptions." },
            { n: "02", t: "We Find Your Match", tag: "Matching", d: "We go through our pre-vetted bench and find the right fit within hours." },
            { n: "03", t: "Meet Your US Partner", tag: "Accountability", d: "Your dedicated US partner is introduced and takes full ownership." },
            { n: "04", t: "We Handle the Onboarding", tag: "Setup", d: "Your professional is briefed, tooled up and ready before day one." },
            { n: "05", t: "They Plug Into Your Workflow", tag: "Day One", d: "No ramp up. No hand holding. Just results from the very first day." },
            { n: "06", t: "Ongoing Support & Oversight", tag: "Continuity", d: "Your US partner stays involved. Performance is monitored so standards never slip." },
            { n: "07", t: "Scale When You're Ready", tag: "Growth", d: "Add more professionals across any function without starting the process over." },
          ].map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-4 px-6 py-5 items-center border-b border-forest-500/8 last:border-0 hover:bg-cream-50">
              <div className="col-span-1 font-serif-italic text-2xl text-forest-400/60">{s.n}</div>
              <div className="col-span-4">
                <div className="font-display font-bold text-forest-600">{s.t}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-forest-400/60 mt-0.5">{s.tag}</div>
              </div>
              <div className="col-span-6 text-sm text-forest-400">{s.d}</div>
              {/* <div className="col-span-1 text-right"><ArrowRight size={16} className="inline text-forest-500" /></div> */}
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE AIDIFY */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="why-aidify">
        <SectionHeading
          label="Why choose aidify"
          title="A partnership built for"
          italic="how you actually work."
          description="Four reasons founders and operators stay with Aidify — beyond the first engagement."
          center
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { n: "01", t: "Scalable Support", d: "Virtual assistants skilled across administrative, executive, financial, marketing, and planning support under one roof, with one point of contact.", dark: true, icon: Users },
            { n: "02", t: "AI-Powered Efficiency", d: "Smarter workflows through automation and modern AI tools, so your team moves faster without compromising quality.", icon: Sparkles },
            { n: "03", t: "Cost-Effective", d: "Affordable global rates with US-quality oversight. Pay only for the capacity you need, scale anytime without overhead.", sage: true, icon: TrendingUp },
            { n: "04", t: "Process-Driven", d: "Every engagement starts with mapping your processes, brand standards and workflow, so day one feels like day one hundred.", icon: Workflow },
          ].map((c, i) => (
            <div key={i} className={`p-7 ${c.dark ? "aidify-card-dark" : c.sage ? "aidify-card-sage" : "aidify-card"}`} data-reveal>
              <div className="flex items-center justify-between mb-5">
                <c.icon size={18} className={c.dark ? "text-sage-200" : "text-forest-500"} />
                <span className={`text-xs uppercase tracking-[0.2em] ${c.dark ? "text-cream-100/60" : "text-forest-400/70"}`}>{c.n}</span>
              </div>
              <h3 className={`font-display font-extrabold text-2xl ${c.dark ? "text-cream-100" : "text-forest-600"}`}>{c.t}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${c.dark ? "text-cream-100/70" : "text-forest-400"}`}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STRATEGIC PARTNERS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="strategic-partners">
        <div className="aidify-card-dark p-8 md:p-12" data-reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <span className="section-pill !bg-forest-500 !text-cream-100/80 !border-forest-400">— Take the conversation further</span>
              <h3 className="mt-5 font-display font-extrabold text-3xl md:text-5xl text-cream-100 leading-tight">
                Connect with our<br/>
                <span className="font-serif-italic font-normal text-sage-200">Strategic Partners</span><br/>
                to build your virtual team.
              </h3>
              <Link to="/contact" className="mt-7 btn-on-dark inline-flex">Meet your virtual team <ArrowRight size={16} /></Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PARTNERS.map((p, i) => (
                <div key={i} className="bg-forest-500/40 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 ring-2 ring-sage-200/20">
                    {p.image ? (
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-sage-200 text-forest-600 flex items-center justify-center font-display font-extrabold">
                        {p.initials}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-cream-100 truncate">{p.name}</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-cream-100/60">{p.role}</div>
                    {/* <div className="text-xs text-sage-200 mt-1 truncate">
                      in <a 
                            href={`https://${p.linkedin}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="hover:underline hover:text-white transition-colors"
                          >
                        {p.linkedin}
                      </a>
                    </div>   */}
                    <div className="mt-2">
                      <a
                        href={`https://${p.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-cream-100/70 hover:text-white transition-colors group/link"
                      >
                        <Linkedin size={12} className="text-sage-200 group-hover/link:text-white transition-colors" />
                        <span className="hover:underline">Connect on LinkedIn</span>
                      </a>
                    </div>
                    
                    {/* <div className="text-xs text-sage-200 mt-1 truncate">in {p.linkedin}</div> */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="target-section"> 
        {/* This is where the Seven Stages are rendered */}
      </section>

      {/* CONTACT FORM */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="home-contact">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel testid="enquire-label">Enquire now</SectionLabel>
            <h2 className="mt-5 font-display font-extrabold text-4xl md:text-6xl text-forest-600 leading-[1.05]">
              Finally a Team
              <br />
              <span className="font-serif-italic font-normal">You Can trust.</span>
            </h2>
            <p className="mt-6 text-forest-400 leading-relaxed text-[15px] max-w-md">
              At Aidify, we don't just assign people. We build the support layer your business has been missing. Whether it's your books, your inbox, your customers or your marketing, we have the right person ready and a US partner making sure they deliver.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-forest-500">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-sage-500" /> Ready in hours, not weeks</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-sage-500" /> Every function. Every channel.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-sage-500" /> Accountability you can actually call</li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ContactForm testid="home-contact-form" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="home-faq">
        <SectionHeading
          label="Frequently asked"
          title="Questions,"
          italic="Clearly answered."
          description="Everything you need to know before getting your first Aidify specialist. Still curious?"
        />
        <FAQAccordion items={COMMON_FAQ} />
      </section>

      <FinalCTA />
    </>
  );
}
